/*!
 * ============================================================================
 *  SCOUT DESIGN STUDIO — ACCESSIBILITY REMEDIATION · v5 ADD-ON (14-Oct-2026)
 *  Fixes for the independent review of 13-Oct-2026 (Eric Vasquez): Bugs 140–143
 *  WCAG 2.2: 2.1.1 Keyboard (A), 2.1.2 No Keyboard Trap (A), 2.4.4 Link Purpose (A),
 *            3.2.5 Change on Request (AAA, the letter's item 2), 2.4.7 Focus Visible (AA),
 *            1.1.1 Non-text Content (A)
 * ----------------------------------------------------------------------------
 *  WHAT THIS IS
 *    Four new SDS.bug blocks in the same format as Bugs 1–139. Written against the
 *    LIVE theme #191402279276 on 14-Oct-2026, which loads, in this order:
 *      assets/ada-pro.js            (1 KB, older jQuery tweaks — see Bug 143)
 *      assets/ada-pro-max-v2.js     (core + Bugs 1–120, 131–139)
 *      assets/a11y-v4-alt-text.js   (Bugs 121–130)
 *    Highest live id is 139, so this file starts at 140.
 *
 *  HOW TO INSTALL — pick ONE
 *    A) Separate file (fastest, nothing else touched):
 *       upload as assets/a11y-v5-review-fixes.js and load it AFTER a11y-v4-alt-text.js
 *       in theme.liquid:
 *         <script src="{{ 'a11y-v5-review-fixes.js' | asset_url }}" defer></script>
 *    B) Paste-in: copy BUG 140 … BUG 143 into ada-pro-max-v2.js above SDS.boot().
 *       If you paste in, ALSO delete the BUG 20 block (see Bug 140 below).
 *    Then, in either case, remove the ada-pro.js <script> tag (Bug 143 replaces it).
 *
 *  ROOT CAUSES FOUND WHILE REPRODUCING THE REVIEW
 *    • The "keyboard trap" on product pages is NOT inside the Emplifi gallery. Tab moves
 *      through the gallery tiles and leaves the iframe normally. It then lands on the
 *      "Powered by Emplifi" link. Emplifi deletes and re-creates that link whenever any
 *      attribute on it changes, and our own Bug 20 changes its attributes on focusin
 *      (to add the new-tab warning). So: focus link → Bug 20 patches it → Emplifi swaps
 *      in a new link → focus falls to <body> at the same spot → next Tab lands on the
 *      new link → repeat. Reproduced on /products/martita-sofa-in-rosewater and
 *      /products/janou-sofa-in-chocolate-stripe (14-Oct, Chrome, real Tab key).
 *    • Until the gallery scrolls into view (and always when a product has no customer
 *      photos) its iframe is 0px tall but still takes keyboard focus, so focus vanishes.
 *    • The unnamed cart photo link: in the cart DRAWER it has no name at all; on the
 *      /cart PAGE our v4 Bug 124 fallback names it "Your cart" (the page heading) — wrong.
 *    • The four new-tab links come from content, not the theme: the product description
 *      metafield ("select fabrics", "California's Proposition 65") and the cart policy
 *      text ("shipping & returns policy", "terms and refund policy"). The only earlier
 *      new-tab fixes were per page (Bugs 22, 43, 56, 64, 66, 67, 72, 74, 83, 87).
 *
 *  Debug:  SDS_A11Y.list()     → confirms 140–143 are registered (Bug 20 shows as RETIRED)
 *          SDS_A11Y.runBug(142) → re-run one block
 * ============================================================================
 */
(function (window, document) {
  'use strict';
  var SDS = window.SDS_A11Y;
  if (!SDS || !SDS.bug) {
    if (window.console) console.warn('[SDS a11y v5] core not found — load ada-pro-max-v2.js first.');
    return;
  }
  var FIRST_NEW_ID = 140;

  // ##########################################################################
  //  INDEPENDENT REVIEW 13-Oct-2026 — FOLLOW-UP FIXES (Bugs 140–143)
  // ##########################################################################

  // ===== BUG 140 — START =====
  // Product (+ any page with the Emplifi/Pixlee UGC gallery) · High · 2.1.1, 2.1.2, 2.4.7
  // Review item: "Keyboard users can't get past the customer photo gallery on product pages."
  // Fix:
  //   1. Retire Bug 20. Its attribute patch on the "Powered by Emplifi" link is what makes
  //      Emplifi rebuild the link under the keyboard focus (see ROOT CAUSES above).
  //      Separate-file mode removes Bug 20 from the run list here, and a capture
  //      listener on window stops its focus/hover/press handlers from reaching the link.
  //   2. The "Powered by Emplifi" link itself is left alone. Untouched, it is stable: it
  //      takes focus once and Tab moves on (verified 14-Oct). It cannot be hidden or
  //      relabelled from the theme: Emplifi puts display/visibility/opacity on it as
  //      inline !important styles and rebuilds it on any attribute change. Its missing
  //      new-tab warning (target="blank") is therefore VENDOR-SIDE. Client action:
  //      switch off "Powered by" branding in the Emplifi dashboard, or ask Emplifi
  //      support to add the warning.
  //   3. Gallery not shown (iframe 0px tall: Emplifi only sizes it once it scrolls into
  //      view, and leaves it at 0px when there are no photos): taken out of the Tab order and the
  //      accessibility tree (inert + tabindex=-1 + aria-hidden), and Bug 99's
  //      "Skip customer photo gallery" link is hidden because there is nothing to skip.
  //      If photos load later and the iframe grows, all of this is undone.
  //   The gallery tiles themselves are unchanged: they are reachable, and Tab leaves the
  //   iframe after the last tile. Bug 99's skip link still lets users jump over them.
  (function () {
    var BRAND = 'a[id^="powered_by_pixlee"]';
    var FRAME = 'iframe[id^="pixlee_widget_iframe"]';

    // Neutralise Bug 20's run() in place. The array is not spliced: in paste-in mode this
    // runs inside the core's bugs.forEach(init), and removing an entry would skip the
    // next block's init.
    function retireBug20() {
      SDS.bugs.forEach(function (b) {
        if (b.id === 20 && !b.retired) {
          b.retired = true;
          b.title = 'RETIRED by Bug 140 (caused the Emplifi keyboard trap)';
          b.def = { run: function () {} };
        }
      });
    }
    function skipLinkFor(frame) {
      var section = frame.closest('.shopify-section') || frame.parentNode;
      var prev = section && section.previousElementSibling;
      return prev && prev.classList.contains('sds-skip-widget') ? prev : null;
    }
    function syncFrame(frame) {
      var empty = frame.getBoundingClientRect().height < 2;
      var skip = skipLinkFor(frame);
      if (empty) {
        if (frame.getAttribute('data-sds-b140') === 'empty') return;
        frame.setAttribute('data-sds-b140', 'empty');
        frame.setAttribute('tabindex', '-1');
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('inert', '');
        if (skip) skip.hidden = true;
      } else if (frame.getAttribute('data-sds-b140') === 'empty') {
        frame.removeAttribute('data-sds-b140');
        frame.removeAttribute('tabindex');
        frame.removeAttribute('aria-hidden');
        frame.removeAttribute('inert');
        if (skip) skip.hidden = false;
      }
    }

    SDS.bug(140, 'Emplifi gallery: no keyboard trap on "Powered by" link, empty gallery not focusable', {
      init: function () {
        retireBug20();
        // Bug 20's listeners sit on document (capture). A window capture listener runs
        // first; stopping propagation here keeps them from touching the link.
        ['focusin', 'mouseover', 'pointerdown', 'touchstart'].forEach(function (ev) {
          window.addEventListener(ev, function (e) {
            if (e.target && e.target.closest && e.target.closest(BRAND)) e.stopPropagation();
          }, true);
        });
        // The iframe changes height when Emplifi finishes loading; watch it.
        if (window.ResizeObserver) {
          var ro = new ResizeObserver(function (entries) {
            entries.forEach(function (en) { syncFrame(en.target); });
          });
          var seen = [];
          SDS.state.b140watch = function () {
            [].forEach.call(document.querySelectorAll(FRAME), function (f) {
              if (seen.indexOf(f) < 0) { seen.push(f); ro.observe(f); }
            });
          };
        }
      },
      run: function () {
        if (SDS.state.b140watch) SDS.state.b140watch();
        [].forEach.call(document.querySelectorAll(FRAME), syncFrame);
      }
    });
  })();
  // ===== BUG 140 — END =====


  // ===== BUG 141 — START =====
  // Cart drawer + /cart page · Medium · 2.4.4 Link Purpose, 1.1.1
  // Review item: "The product photo in the cart is an unnamed link."
  // Drawer: <a class="cart-item__image-link"><img alt=""></a> → announced as just "link".
  // /cart page: the same link is named "Your cart" by Bug 124's generic fallback.
  // Fix: name the photo link after the product (the text of the title link in the same
  //      cart row, which already includes the colour), and keep the image decorative.
  //      Runs after Bug 124 on every pass, so it always wins.
  //   Also recommended (one-line change in a11y-v4-alt-text.js, Bug 124): add
  //   .cart-item to the exclusion list →
  //     if (a.closest('product-block, media-gallery, .thumbnails, .cart-item')) return;
  (function () {
    function titleFor(link) {
      var row = link.closest('.cart-item') || link.parentNode.parentNode;
      if (!row) return '';
      var path = link.pathname;
      var match = [].filter.call(row.querySelectorAll('a[href]'), function (a) {
        return a !== link && a.pathname === path && SDS.text(a);
      })[0];
      return SDS.text(match) || SDS.text(row.querySelector('.cart-item__title, .cart-item__name'));
    }
    SDS.bug(141, 'Cart: product photo links named after the product', {
      run: function () {
        [].forEach.call(document.querySelectorAll('a.cart-item__image-link'), function (a) {
          var name = titleFor(a);
          if (!name) return;
          if (a.getAttribute('aria-label') !== name) a.setAttribute('aria-label', name);
          [].forEach.call(a.querySelectorAll('img'), function (img) {
            if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');
          });
        });
      }
    });
  })();
  // ===== BUG 141 — END =====


  // ===== BUG 142 — START =====
  // Global · High · 3.2.5 Change on Request, 2.4.4 (demand letter item 2)
  // Review item: four links still open a new tab with no warning:
  //   • "terms and refund policy"   — cart drawer (/policies/terms-of-service) and
  //                                   /cart page (/pages/terms-conditions), inside the
  //                                   label of the "I agree" checkbox
  //   • "shipping & returns policy" — cart drawer
  //   • "select fabrics"            — product description (upholstered pieces)
  //   • "California's Proposition 65" — product description / Prop 65 disclosure
  // The last two come from the shared description text, so they are on many products.
  // Fix: one site-wide sweep instead of per-page blocks. Every link that opens a new tab
  //      (target="_blank", and the invalid target="blank") and does not already say so
  //      gets SDS.newTab(): visible text stays first, " (opens in a new tab)" is added as
  //      screen-reader text, rel="noopener" is added. New content (cart drawer refresh,
  //      quick buy, Depict grid) is covered on the next pass.
  //   Skipped: third-party widgets that rebuild their own markup (Emplifi, TurnTo,
  //   Klaviyo). Adding nodes inside them causes the same rebuild loop as Bug 20.
  //   Content fix (lasting): remove "Open in new window" from these links in the
  //   product description metafield/template and in Theme settings › Cart, so they open
  //   in the same tab. This block then simply stops matching.
  (function () {
    var SKIP = '[id^="pixlee_container"], .tt-container, .tt-o-modal, form.klaviyo-form, ' +
               '[data-testid="POPUP"], [data-testid="FLYOUT"], [id^="shopify-chat"], inbox-online-store-chat';
    function says(a) {
      var name = (a.getAttribute('aria-label') || '') + ' ' + SDS.text(a) + ' ' + (a.getAttribute('title') || '');
      return /new (tab|window)/i.test(name);
    }
    SDS.bug(142, 'Site-wide new-tab warning for links that open a new tab', {
      run: function () {
        [].forEach.call(document.querySelectorAll('a[target="_blank"], a[target="blank"]'), function (a) {
          if (a.getAttribute('data-sds-newtab') || a.closest(SKIP)) return;
          if (!a.getAttribute('href') || /^(mailto|tel):/i.test(a.getAttribute('href'))) return;
          if (says(a)) { a.setAttribute('data-sds-newtab', '1'); return; }
          var named = SDS.text(a) || a.getAttribute('aria-label') || a.getAttribute('aria-labelledby') ||
                      [].some.call(a.querySelectorAll('img'), function (i) { return i.getAttribute('alt'); });
          if (!named) return;                    // nameless links are handled by their own blocks
          SDS.newTab(a);
        });
      }
    });
  })();
  // ===== BUG 142 — END =====


  // ===== BUG 143 — START =====
  // Global · Medium · 1.1.1, 1.3.1, 2.4.4 — replaces assets/ada-pro.js
  // Review item: "The report suggests removing ada-pro.js … the transfer guide lists it as
  // one of the files to keep … confirm which setup is intended."
  // Finding (14-Oct): the live ada-pro.js is no longer the old large file the report
  // warned about (the one that made controls fire twice). It is now 1 KB of jQuery
  // tweaks that run once, 4 s after load. Some of them now conflict with the main bundle:
  //   a) sets alt="Warning" on the Prop 65 warning icon → "Warning, WARNING!" (Bug 121
  //      makes it decorative, alt="", because the word WARNING follows it in text)
  //   b) labels every hello@ email link "Email privacy questions to …, opens in a new tab"
  //      on every page. Email links do not open a tab, and the wording only fits
  //      the Privacy page (Bug 78 already labels that one)
  //   c) labels .text-column__button.small-feature-link "…, opens in a new tab" (same issue)
  //   d) its "Accessibility Statement" skip link never appears: it looks for
  //      .skip-link[data-cs-role="skip"] and the theme's skip link has no such attribute
  // The useful parts are kept here (decorative images in video components and image
  // columns, removal of empty headings such as the empty H1 .block-heading_mHx3DT on the
  // homepage), the conflicting parts are corrected, and the dead skip link is dropped.
  // DECISION: after this block is live, remove ada-pro.js from theme.liquid. Final set of
  // files = ada-pro-max-v2.js + a11y-v4-alt-text.js + a11y-v5-review-fixes.js.
  // (If ada-pro.js is still loaded, this block corrects its output after it runs.)
  (function () {
    var THIRD_PARTY = '.tt-container, .tt-o-modal, [id^="pixlee_container"], .klaviyo-form, [role="dialog"]';
    SDS.bug(143, 'ada-pro.js tweaks folded into the bundle (conflicts corrected)', {
      run: function () {
        // decorative images (from ada-pro.js)
        [].forEach.call(document.querySelectorAll('video-component img, .text-column__image.lightly-spaced-row img'), function (img) {
          if (img.closest('a') && !SDS.text(img.closest('a')) && !img.closest('a').getAttribute('aria-label')) return; // the image names its link
          if (img.getAttribute('alt') !== '') img.setAttribute('alt', '');
        });
        // a) Prop 65 warning icon stays decorative even if ada-pro.js set alt="Warning"
        [].forEach.call(document.querySelectorAll('img'), function (img) {
          if (/warning[^\/]*\.svg|\/warning_\d+x\d+/i.test(img.getAttribute('src') || '') && img.getAttribute('alt') === 'Warning') img.setAttribute('alt', '');
        });
        // b) + c) email links never "open in a new tab"
        [].forEach.call(document.querySelectorAll('a[aria-label]'), function (a) {
          var href = a.getAttribute('href') || '';
          var label = a.getAttribute('aria-label');
          if (/^(mailto|tel):/i.test(href) && a.getAttribute('target') !== '_blank' && /,?\s*opens in a new tab\s*$/i.test(label)) {
            label = label.replace(/,?\s*opens in a new tab\s*$/i, '');
            // the site-wide privacy wording only belongs on the Privacy Policy page
            if (/^Email privacy questions to /.test(label) && !SDS.isPage('privacy-policy')) label = label.replace(/^Email privacy questions to /, 'Email ');
            a.setAttribute('aria-label', label);
          }
        });
        // empty headings (from ada-pro.js), limited to theme content, not app widgets
        [].forEach.call(document.querySelectorAll('main h1, main h2, main h3, main h4, main h5, main h6, #pagefooter h2, #pagefooter h3, #pagefooter h4'), function (h) {
          if (h.closest(THIRD_PARTY) || h.id === 'sds-page-title') return;
          if (SDS.text(h) || h.querySelector('img[alt]:not([alt=""]), svg[aria-label]')) return;
          h.parentNode.removeChild(h);
        });
      }
    });
  })();
  // ===== BUG 143 — END =====


  // ==========================================================================
  //  SEPARATE-FILE MODE ONLY (ignore when the blocks are pasted into the main file)
  //  If the core has already booted (SDS.$ is set), inject each new block's CSS,
  //  run its init once, then run it now and on every later refresh pass.
  // ==========================================================================
  if (SDS.$) {
    SDS.bugs.slice().forEach(function (b) {
      if (b.id < FIRST_NEW_ID) return;
      try {
        SDS.css('sds-a11y-bug-' + b.id, b.def.css);
        if (b.def.init) b.def.init.call(b, SDS.$);
        SDS.runBug(b.id);
      } catch (err) { if (window.console) console.warn('[SDS a11y v5] Bug ' + b.id + ' failed:', err); }
    });
  }
})(window, document);






/*!
 * ============================================================================
 *  SCOUT DESIGN STUDIO — ACCESSIBILITY REMEDIATION · v5.1 HOTFIX (19-Oct-2026)
 *  Fixes three problems found in the live recheck of all 145 bug IDs on 19-Oct-2026.
 *  No new bug IDs: this revises Bugs 122, 137 and 143 in place.
 *  WCAG 2.2: 1.1.1 Non-text Content (A), 2.1.1 Keyboard (A), 4.1.2 Name, Role, Value (A)
 * ----------------------------------------------------------------------------
 *  1) Bug 84 regression (/pages/influencer-program)
 *     Bug 143 copied the ada-pro.js rule "every image in video-component and
 *     .text-column__image.lightly-spaced-row is decorative". On the Influencer
 *     page that row holds the "From Our Partners" photos, which Bug 84 names.
 *     Bug 143 runs after Bug 84 on every pass, so their alt went back to "".
 *     ada-pro.js (still loaded on live) does the same thing again ~4 s after load.
 *     FIX: Bug 143 now only blanks an alt that is junk (empty, a file name, a
 *     "#color_…" code) or that a later SDS alt map has not named. Images named by
 *     any SDS alt map are tagged data-sds-alt and left alone. A refresh pass runs
 *     after ada-pro.js's timer so any alt it blanks is put back.
 *
 *  2) Bug 122 reopened by a content change (homepage hero)
 *     The slideshow image was replaced after 6-Oct with Welcome_to_Your_Life_7
 *     (button "SEE WHAT'S NEW"). It has alt="" and contains the words
 *     "Artwork Restock", so the text in the image is lost to screen readers.
 *     FIX: Bug 122 gets a fallback alt for this file.
 *     BETTER: add the alt in Shopify admin → Online Store → Customize → Slideshow →
 *     image → "Alt text" (the theme then outputs it and no script is needed).
 *
 *  3) Bug 137 partly failing (cart drawer "You've got bills to pay" carousel)
 *     Two products are fully visible but only the first is exposed; the second is
 *     aria-hidden and its link has tabindex="-1" (the theme's own visibility check is
 *     wrong in the drawer). FIX: Bug 137 measures the slides itself.
 *
 *  HOW TO INSTALL
 *    Paste this whole file at the END of assets/a11y-v5-retesting.js (the v5 file
 *    on live), or upload it as assets/a11y-v5-1-hotfix.js and load it after that file:
 *      <script src="{{ 'a11y-v5-1-hotfix.js' | asset_url }}" defer></script>
 *    STRONGLY RECOMMENDED: remove the ada-pro.js <script> tag from theme.liquid.
 *    Everything it did is now in Bug 143, and it is what keeps undoing Bug 84.
 *
 *  Check:  SDS_A11Y.runBug(84); SDS_A11Y.runBug(143);
 *          document.querySelectorAll('[data-sds-alt]').length   → > 0 on /pages/influencer-program
 * ============================================================================
 */
(function (window, document) {
  'use strict';
  var SDS = window.SDS_A11Y;
  if (!SDS || !SDS.bug || !SDS.bugs) {
    if (window.console) console.warn('[SDS a11y v5.1] core not found — load after ada-pro-max-v2.js and a11y-v5.');
    return;
  }

  function findBug(id) {
    for (var i = 0; i < SDS.bugs.length; i++) if (SDS.bugs[i].id === id) return SDS.bugs[i];
    return null;
  }

  // --- A. Tag every image an SDS alt map names, so later rules leave it alone ---
  if (!SDS.altByFile.__sdsTagged) {
    var origAltByFile = SDS.altByFile;
    SDS.altByFile = function (map, scope) {
      origAltByFile.apply(this, arguments);
      var root = typeof scope === 'string' ? document.querySelector(scope) : (scope || document);
      if (!root) return;
      [].forEach.call(root.querySelectorAll('img'), function (img) {
        var src = (img.getAttribute('src') || '') + ' ' + (img.getAttribute('data-src') || '') + ' ' + (img.getAttribute('srcset') || '');
        for (var key in map) {
          if (Object.prototype.hasOwnProperty.call(map, key) && src.indexOf(key) > -1) {
            img.setAttribute('data-sds-alt', '1');
            if (img.getAttribute('alt') !== map[key]) img.setAttribute('alt', map[key]);
            break;
          }
        }
      });
    };
    SDS.altByFile.__sdsTagged = true;
  }

  // --- B. Bug 143 (rev. 1): only blank alts that are junk or were never named by SDS ---
  var b143 = findBug(143);
  if (b143) {
    var origRun143 = b143.def.run;
    b143.def.run = function () {
      // remember named alts, let the original rules run, then put the named ones back
      var keep = [];
      [].forEach.call(document.querySelectorAll('img[data-sds-alt]'), function (img) {
        var alt = img.getAttribute('alt');
        if (SDS.cleanAlt(alt) !== '') keep.push([img, alt]);
      });
      if (origRun143) origRun143.apply(this, arguments);
      keep.forEach(function (k) { if (k[0].getAttribute('alt') !== k[1]) k[0].setAttribute('alt', k[1]); });
    };
  }

  // --- C. Bug 122 (rev. 1): fallback alt for the new homepage hero ---
  var b122 = findBug(122);
  if (b122) {
    var origRun122 = b122.def.run;
    var HERO = {
      'Welcome_to_Your_Life_7_20b363ad': 'Artwork Restock: four framed cowboy boot prints hung above a grey console styled with cowboy boots, bottles and a vase of sunflowers'
    };
    b122.def.run = function () {
      if (origRun122) origRun122.apply(this, arguments);
      if (!SDS.isTemplate('index')) return;
      [].forEach.call(document.querySelectorAll('.section-slideshow img'), function (img) {
        var src = (img.getAttribute('src') || '') + ' ' + (img.getAttribute('srcset') || '');
        for (var key in HERO) {
          // only fill when the merchant has not added an alt in the theme editor
          if (src.indexOf(key) > -1 && SDS.cleanAlt(img.getAttribute('alt')) === '') {
            img.setAttribute('alt', HERO[key]);
            img.setAttribute('data-sds-alt', '1');
          }
        }
      });
    };
  }

  // --- D. Bug 137 (rev. 1): cart drawer recommendations — every slide in view is "current" ---
  //     The theme's carousel-slider.getSlideVisibility() reports only the FIRST slide as
  //     visible in the cart drawer, although two slides (2 per row) are fully on screen.
  //     Bug 137 trusted it, so the second visible product was aria-hidden and its link
  //     had tabindex="-1": keyboard and screen reader users could not reach it.
  //     Measure the slide against the slider box instead (≥ 50 % on screen = visible).
  var b137 = findBug(137);
  if (b137) {
    var origRun137 = b137.def.run;
    var geo = function (car) {
      if (car.__sdsGeo) return;
      car.__sdsGeo = true;
      car.getSlideVisibility = function (li) {
        var box = (car.querySelector('.slider') || car).getBoundingClientRect();
        var r = li.getBoundingClientRect();
        if (!r.width) return false;
        return Math.min(r.right, box.right) - Math.max(r.left, box.left) >= r.width * 0.5;
      };
    };
    b137.def.run = function () {
      [].forEach.call(document.querySelectorAll('carousel-slider[data-sds-b137], .cart-item-upsells carousel-slider'), geo);
      if (origRun137) return origRun137.apply(this, arguments);
    };
  }

  // --- E. Run now, and again after ada-pro.js's 4-second timer ---
  function pass() {
    try { SDS.runBug(84); SDS.runBug(122); SDS.runBug(137); SDS.runBug(143); } catch (e) {}
  }
  if (SDS.$) pass();
  window.setTimeout(pass, 4500);
  window.setTimeout(pass, 9000);
})(window, document);
